import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Building2, User, Mail, Phone, Lock, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { ConsentCheckbox } from '../components/legal/ConsentCheckbox';
import { getRequiredAtRegistration, recordConsent } from '../api/legal';

const FALLBACK_PLANS = [
  { name: 'starter',  display_name: 'Starter',  price_monthly: 799,  price_yearly: 7668,  yearly_discount_percent: 20, max_users: 5 },
  { name: 'business', display_name: 'Business', price_monthly: 1499, price_yearly: 14388, yearly_discount_percent: 20, max_users: 20 },
  { name: 'pro',      display_name: 'Pro',      price_monthly: 2499, price_yearly: 23988, yearly_discount_percent: 20, max_users: 50 },
];

export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loadUser } = useAuth();

  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [form, setForm] = useState({
    company_name: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirm: '',
    plan: searchParams.get('plan') || 'starter',
  });
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [consents, setConsents] = useState([]);
  const [checkedConsents, setCheckedConsents] = useState({});

  // API'den planları çek
  useEffect(() => {
    api.get('/plans').then(r => {
      if (r.data?.plans?.length) setPlans(r.data.plans);
    }).catch(() => {});
  }, []);

  // Sözleşmeleri yükle
  useEffect(() => {
    getRequiredAtRegistration()
      .then(d => setConsents(d.documents || []))
      .catch(() => {});
  }, []);

  const allConsented = consents.length === 0 || consents.every(c => checkedConsents[c.slug]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.company_name.trim()) return setError('Firma adı gerekli');
    if (!form.name.trim()) return setError('Ad soyad gerekli');
    if (!form.email.trim()) return setError('E-posta gerekli');
    if (form.password.length < 6) return setError('Şifre en az 6 karakter olmalı');
    if (form.password !== form.password_confirm) return setError('Şifreler eşleşmiyor');
    if (!allConsented) return setError('Sözleşmeleri kabul etmelisiniz');

    setLoading(true);
    try {
      const { data } = await api.post('/auth/register', {
        company_name: form.company_name,
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        password: form.password,
        plan: form.plan,
      });

      // Otomatik giriş
      localStorage.setItem('token', data.token);

      // Token sonrası onayları kaydet (sessiz fail — reconsent middleware sonra yakalar)
      try {
        for (const doc of consents) {
          await recordConsent({
            document_id: doc.id,
            context: 'registration',
          });
        }
      } catch {
        /* sessiz */
      }

      await loadUser();
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Kayıt yapılamadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-block mb-3">
            <svg width="48" height="48" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="32" height="32" rx="10" fill="#0A0F1E"/>
              <circle cx="16" cy="14" r="10" stroke="#2563EB" strokeOpacity="0.15" strokeWidth="0.75"/>
              <circle cx="16" cy="14" r="6.5" stroke="#2563EB" strokeOpacity="0.25" strokeWidth="0.75"/>
              <circle cx="16" cy="14" r="3" stroke="#2563EB" strokeOpacity="0.35" strokeWidth="0.75"/>
              <line x1="6" y1="14" x2="26" y2="14" stroke="#2563EB" strokeOpacity="0.12" strokeWidth="0.5"/>
              <line x1="16" y1="4" x2="16" y2="24" stroke="#2563EB" strokeOpacity="0.12" strokeWidth="0.5"/>
              <circle cx="16" cy="14" r="4" fill="#2563EB"/>
              <circle cx="16" cy="14" r="1.5" fill="white"/>
              <polygon points="16,21 13.5,16 18.5,16" fill="#2563EB"/>
            </svg>
          </div>
          <h1 className="text-2xl font-extrabold" style={{letterSpacing: '-1px'}}>
            <span className="text-white">field</span><span className="text-blue-500">pin</span>
          </h1>
          <p className="text-slate-400 text-sm mt-1">Saha ekibi yönetim platformu</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Hesap Oluşturun</h2>
          <p className="text-sm text-slate-500 mb-6">14 gün ücretsiz, kredi kartı gerekmez.</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Plan seçimi */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Plan Seçin</label>
              {/* Aylık / Yıllık toggle */}
              <div className="flex items-center justify-center gap-3 mb-3">
                <span className={`text-xs font-semibold ${billingCycle === 'monthly' ? 'text-slate-800' : 'text-slate-400'}`}>Aylık</span>
                <button type="button" onClick={() => setBillingCycle(c => c === 'monthly' ? 'yearly' : 'monthly')}
                  className="w-11 h-6 rounded-full bg-slate-200 relative transition">
                  <div className="rounded-full bg-blue-600 absolute top-[3px] transition-transform" style={{width:18, height:18, transform: billingCycle === 'yearly' ? 'translateX(22px)' : 'translateX(3px)'}} />
                </button>
                <span className={`text-xs font-semibold ${billingCycle === 'yearly' ? 'text-slate-800' : 'text-slate-400'}`}>Yıllık</span>
                {billingCycle === 'yearly' && (
                  <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    %{plans[0]?.yearly_discount_percent || 20} tasarruf
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {plans.filter(p => p.name !== 'enterprise').map(p => {
                  const monthly = Math.round(parseFloat(p.price_monthly));
                  const disc = p.yearly_discount_percent || 20;
                  const yearlyEquiv = Math.round(monthly * (1 - disc / 100));
                  const yearlyTotal = p.price_yearly ? Math.round(parseFloat(p.price_yearly)) : yearlyEquiv * 12;
                  const displayPrice = billingCycle === 'yearly' ? yearlyEquiv : monthly;
                  const isPopular = p.name === 'business';
                  const userLabel = p.max_users > 50 ? 'Sınırsız kullanıcı' : `${p.max_users} kullanıcı`;
                  return (
                    <button
                      key={p.name}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, plan: p.name }))}
                      className={`relative p-3 rounded-xl border-2 text-center transition ${
                        form.plan === p.name
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {isPopular && (
                        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-blue-600 text-white text-[9px] font-bold rounded-full">
                          Popüler
                        </span>
                      )}
                      <p className={`text-sm font-semibold ${form.plan === p.name ? 'text-blue-700' : 'text-slate-700'}`}>
                        {p.display_name}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{userLabel}</p>
                      <p className={`text-base font-bold mt-1 ${form.plan === p.name ? 'text-blue-600' : 'text-slate-800'}`}>
                        {displayPrice.toLocaleString('tr-TR')} <span className="text-xs font-normal text-slate-400">TL/ay</span>
                      </p>
                      {billingCycle === 'yearly' && (
                        <div className="mt-1">
                          <p className="text-[10px] text-slate-500">
                            Yıllık toplam: <span className="font-semibold text-slate-700">{yearlyTotal.toLocaleString('tr-TR')} TL</span>
                          </p>
                          <span className="inline-block mt-0.5 text-[9px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                            %{disc} indirim
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Firma adı */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Firma Adı</label>
              <div className="relative">
                <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={form.company_name}
                  onChange={set('company_name')}
                  placeholder="Şirket veya işletme adı"
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                />
              </div>
            </div>

            {/* Ad soyad + Telefon */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Ad Soyad</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={form.name}
                    onChange={set('name')}
                    placeholder="Yetkili kişi"
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Telefon</label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={form.phone}
                    onChange={set('phone')}
                    placeholder="05xx xxx xx xx"
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>
            </div>

            {/* E-posta */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">E-posta</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="yetkili@firmaniz.com"
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                />
              </div>
            </div>

            {/* Şifreler */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Şifre</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    value={form.password}
                    onChange={set('password')}
                    placeholder="En az 6 karakter"
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                    minLength={6}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Şifre Tekrar</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    value={form.password_confirm}
                    onChange={set('password_confirm')}
                    placeholder="Tekrar girin"
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                    minLength={6}
                  />
                </div>
              </div>
            </div>

            {/* Sözleşme onayları */}
            {consents.length > 0 && (
              <div className="space-y-2.5 border-t border-slate-200 pt-4">
                {consents.map(doc => (
                  <ConsentCheckbox
                    key={doc.slug}
                    document={doc}
                    checked={!!checkedConsents[doc.slug]}
                    onChange={(v) => setCheckedConsents(prev => ({ ...prev, [doc.slug]: v }))}
                  />
                ))}
              </div>
            )}

            {/* Trial notu */}
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
              <Check size={16} className="text-green-600 shrink-0 mt-0.5" />
              <p className="text-xs text-green-700 leading-relaxed">
                14 gün boyunca tüm özellikleri ücretsiz kullanın. Kredi kartı bilgisi gerekmez. İstediğiniz zaman iptal edebilirsiniz.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !allConsented}
              className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
              ) : (
                <>Ücretsiz Başla</>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-5">
            Zaten hesabınız var mı?{' '}
            <Link to="/login" className="text-blue-600 font-medium hover:underline">Giriş Yap</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
